import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { AuthShell, Field, inputCls, SubmitBtn } from "@/components/student/AuthShell";
import { PasswordInput } from "@/components/student/PasswordInput";
import { apiError, useStudentAuth } from "@/lib/student-auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULES = [
  { id: "len", label: "At least 8 characters", test: (v) => v.length >= 8 },
  { id: "upper", label: "1 uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { id: "num", label: "1 number", test: (v) => /\d/.test(v) },
];

export default function StudentSetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setStudent } = useStudentAuth();

  const [account, setAccount] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setInvalid("This link is missing its setup code. Please use the link from your email.");
      return;
    }
    axios
      .get(`${API}/student/activation/${token}`)
      .then((r) => setAccount(r.data))
      .catch((err) => setInvalid(apiError(err?.response?.data?.detail, "This setup link is no longer valid.")));
  }, [token]);

  const allOk = RULES.every((r) => r.test(form.password));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) {
      setError("Please meet all password requirements.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/student/activate`,
        { token, password: form.password },
        { withCredentials: true },
      );
      setStudent(data.student);
      toast.success(`Welcome aboard, ${data.student?.name?.split(" ")[0] || "student"}!`);
      nav("/student", { replace: true });
    } catch (err) {
      setError(apiError(err?.response?.data?.detail, "Could not create your account"));
    } finally {
      setLoading(false);
    }
  };

  if (invalid) {
    return (
      <AuthShell
        testid="set-password-invalid"
        eyebrow="Account setup"
        title="Link no longer valid."
        subtitle={invalid}
        footer={
          <Link to="/student/login" data-testid="invalid-to-login" className="text-[#06B6D4] font-medium">
            Go to login
          </Link>
        }
      >
        <p className="text-sm text-slate-400 leading-relaxed">
          If you already set a password, log in normally. Otherwise use{" "}
          <Link to="/student/forgot-password" className="text-[#06B6D4]">
            Forgot password
          </Link>{" "}
          with the email you paid with.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testid="set-password-page"
      eyebrow="Account setup — last step!"
      title="Create your password."
      subtitle="Pick a password you'll remember. You'll be logged straight into your dashboard."
    >
      <form onSubmit={submit} data-testid="set-password-form">
        <Field label="Email ID">
          <input
            data-testid="setpw-email"
            type="email"
            readOnly
            value={account?.email || ""}
            className={`${inputCls} opacity-60 cursor-not-allowed`}
          />
        </Field>
        <Field label="Create your password">
          <PasswordInput
            testid="setpw-password"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls}
            placeholder="••••••••"
          />
        </Field>

        <ul data-testid="setpw-rules" className="mb-4 space-y-1.5">
          {RULES.map((r) => {
            const ok = r.test(form.password);
            return (
              <li
                key={r.id}
                data-testid={`setpw-rule-${r.id}`}
                className={`flex items-center gap-2 text-[11px] ${ok ? "text-emerald-400" : "text-slate-500"}`}
              >
                {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {r.label}
              </li>
            );
          })}
        </ul>

        <Field label="Confirm password">
          <PasswordInput
            testid="setpw-confirm"
            required
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className={inputCls}
            placeholder="••••••••"
          />
        </Field>

        {error && (
          <div
            data-testid="setpw-error"
            className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-200"
          >
            {error}
          </div>
        )}

        <SubmitBtn loading={loading || !account} testid="setpw-submit">
          Create Account & Login
        </SubmitBtn>
      </form>
    </AuthShell>
  );
}
